import { useState } from 'react';
import { Box, Stack, Paper, Divider, Typography, Drawer } from '@mui/material';
import { useFieldArray, type Control, type FieldErrors } from 'react-hook-form';

import {
    SimpleButton,
    ControlledSingleSelect,
    ControlledChipMultiSelect,
    ControlledSwitch,
    ErrorMessage,
} from '@/src/ui';
import {
    type QtiDragAndDropQuestionDTO,
    type ScoringRule,
    type DragChoice,
    type DropZone,
} from '@/src/lib';

interface DDScoringEditorProps {
    control: Control<QtiDragAndDropQuestionDTO>;
    errors: FieldErrors<QtiDragAndDropQuestionDTO>;
    zones: DropZone[];
    choices: DragChoice[];
    scoringRules: ScoringRule[];
}

export default function ScoringRulesEditorRHF({
    control,
    errors,
    zones,
    choices,
    scoringRules,
}: DDScoringEditorProps) {
    const { fields, append, remove, insert } = useFieldArray({
        control,
        name: 'scoringRules',
    });

    const [selectedIndex, setSelectedIndex] = useState<number | null>(
        scoringRules.length ? 0 : null
    );

    const selectedRule = selectedIndex != null ? scoringRules[selectedIndex] : null;

    const addRule = () => {
        const defaultZone = zones[0]?.id ?? '';
        append({
            zoneId: defaultZone,
            choiceIds: defaultZone ? [] : [], // author must pick
            matchMode: 'any',
            weight: 1,
            orderSensitive: false,
        } as any);
        setSelectedIndex(scoringRules.length);
    };

    const duplicateSelected = () => {
        if (selectedIndex == null || !selectedRule) return;
        insert(selectedIndex + 1, { ...selectedRule } as any);
        setSelectedIndex(selectedIndex + 1);
    };

    const removeSelected = () => {
        if (selectedIndex == null) return;
        remove(selectedIndex);
        setSelectedIndex((prev) => {
            if (prev == null) return null;
            const newLen = scoringRules.length - 1;
            if (newLen <= 0) return null;
            return Math.max(0, Math.min(prev, newLen - 1));
        });
    };

    return (
        <Drawer
            variant="permanent"
            anchor="right"
            slotProps={{ paper: { sx: { position: 'static', minWidth: 700, p: 0 } } }}
            open
        >
            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6">Scoring Rules</Typography>

                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="flex-start"
                    sx={{ mt: 1, flex: 1, minHeight: 0 }}
                >
                    {/* Left: rule list */}
                    <Stack spacing={1} sx={{ width: 300, minWidth: 300 }}>
                        <SimpleButton color="primary" onClick={addRule}>
                            Add Rule
                        </SimpleButton>
                        <ErrorMessage error={errors.scoringRules} />

                        <Stack spacing={1} sx={{ overflowY: 'auto', maxHeight: '60vh' }}>
                            {fields.map((f, i) => {
                                const isSelected = i === selectedIndex;
                                const ruleError = errors.scoringRules?.[i];
                                const zoneLabel =
                                    zones.find((z) => z.id === scoringRules[i]?.zoneId)?.label ??
                                    scoringRules[i]?.zoneId ??
                                    '(no zone)';
                                const chip = (scoringRules[i]?.matchMode ?? 'any').toUpperCase();

                                return (
                                    <Paper
                                        key={f.id}
                                        variant="outlined"
                                        sx={{
                                            p: 1,
                                            border: isSelected
                                                ? '2px solid #1976d2'
                                                : '1px solid #CCCCCC',
                                            cursor: 'pointer',
                                            boxShadow: isSelected ? 4 : 0,
                                            transition: 'border 0.2s, box-shadow 0.2s',
                                        }}
                                        onClick={() => setSelectedIndex(i)}
                                    >
                                        <Stack spacing={0.5}>
                                            <Typography variant="subtitle2">
                                                Rule {i + 1} — {zoneLabel}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Mode: {chip} • Choices:{' '}
                                                {scoringRules[i]?.choiceIds?.length ?? 0} • Weight:{' '}
                                                {scoringRules[i]?.weight ?? 0}
                                            </Typography>

                                            {ruleError && (
                                                <Typography variant="caption" color="error">
                                                    Errors need to be corrected
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Paper>
                                );
                            })}
                            {fields.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No scoring rules yet. Click <b>Add Rule</b> to get started.
                                </Typography>
                            )}
                        </Stack>
                    </Stack>

                    <Divider orientation="vertical" flexItem />

                    {/* Right: selected rule editor */}
                    <Stack spacing={2} flex={1} key={selectedIndex ?? 'none'}>
                        {selectedIndex != null && selectedRule ? (
                            <>
                                <Stack direction="row" spacing={1}>
                                    <SimpleButton color="error" onClick={removeSelected}>
                                        Delete
                                    </SimpleButton>
                                    <SimpleButton onClick={duplicateSelected}>Copy</SimpleButton>
                                </Stack>

                                <ControlledSingleSelect
                                    name={`scoringRules.${selectedIndex}.zoneId`}
                                    control={control}
                                    options={zones.map((z) => ({ id: z.id, name: z.label }))}
                                    label="Zone"
                                    error={errors.scoringRules?.[selectedIndex]?.zoneId}
                                />

                                <ControlledChipMultiSelect
                                    name={`scoringRules.${selectedIndex}.choiceIds`}
                                    control={control}
                                    options={choices.map((c) => ({ id: c.id, name: c.label }))}
                                    label="Correct choice(s)"
                                    error={errors.scoringRules?.[selectedIndex]?.choiceIds}
                                />

                                <ControlledSingleSelect
                                    name={`scoringRules.${selectedIndex}.matchMode`}
                                    control={control}
                                    options={['Any', 'All', 'None'].map((m) => ({
                                        id: m.toLowerCase(),
                                        name: m,
                                    }))}
                                    label="Match mode"
                                    error={errors.scoringRules?.[selectedIndex]?.matchMode}
                                />

                                {scoringRules[selectedIndex]?.matchMode === 'all' && (
                                    <ControlledSwitch
                                        name={`scoringRules.${selectedIndex}.orderSensitive`}
                                        control={control}
                                        label={`Order sensitive`}
                                    />
                                )}
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Select a rule to edit its properties.
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </Box>
        </Drawer>
    );
}
